import { FC, ReactNode, useContext, useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { observer } from 'mobx-react'
import './SignProgress.css'
import { cx } from '@/utils/cx'
import { StoreContext } from '@store/Root.store'
import { Document, GroupWithFiles } from '@components/SignDocumentCommon/types'

interface SignGroupedProgressProps {
  children?: ReactNode
  currentPage: number
  documentList: Document[]
  setContainerHeight?: (value: number) => void
  showSubHeader?: boolean
  groups: GroupWithFiles[]
}

export const SignGroupedProgress: FC<SignGroupedProgressProps> = observer(
  ({ children, currentPage, documentList, setContainerHeight, groups }) => {
    const store = useContext(StoreContext)
    const { signDocumentPage: trans } = store.TranslationsState.translations

    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (containerRef.current && documentList?.length) {
        setContainerHeight && setContainerHeight(containerRef?.current?.clientHeight + 10)
      }
    }, [containerRef.current, documentList])

    const docName = documentList?.at(currentPage - 1)?.name
    const docGroupId = documentList?.at(currentPage - 1)?.groupId
    const currentGroupIndex = groups?.findIndex((g) => g.groupId === docGroupId)

    if (currentGroupIndex < 0) return null

    const currentGroup = currentGroupIndex + 1
    const docDescription = documentList?.at(currentPage - 1)?.description
    const currentDocInGroupIndex = groups[currentGroupIndex].files.findIndex(
      (f) => f.fileId === documentList?.at(currentPage - 1)?.fileId
    )
    const currentDocInGroup = currentDocInGroupIndex + 1
    const progressPercent = Math.min(Math.max(currentGroup / groups?.length, 0), 1) * 100

    return (
      <div className="sign-progress__container" ref={containerRef}>
        <div className={cx('sign-progress__grid', 'sign-progress__grid--grouped')}>
          <div className="sign-progress__group">
            {trans.signProgress.transaction} {currentGroup} / {groups?.length}
          </div>

          <div className="sign-progress__reference-id">
            {trans.signProgress.referenceId}: <span>{docDescription}</span>
          </div>

          {groups?.length <= 5 ? (
            <div className="sign-progress__bars-container">
              {groups.map((group, index) => {
                return (
                  <div
                    key={group.groupId}
                    className={cx(
                      'sign-progress__single-bar',
                      index + 1 <= currentGroup && 'sign-progress__single-bar--active'
                    )}
                  />
                )
              })}
            </div>
          ) : (
            <div className="sign-progress__bar">
              <div
                className="sign-progress__filled-bar"
                style={{ '--sign-progress-fill': `${progressPercent}%` } as CSSProperties}
              />
            </div>
          )}

          <div className="sign-progress__documents" data-testid="sign-progress-documents">
            {groups[currentGroupIndex]?.files.map((file, index) => (
              <div
                className={cx(
                  'sign-progress__documents--item',
                  currentDocInGroupIndex > index && 'signed',
                  currentDocInGroupIndex === index && 'active'
                )}
                key={file.fileId}
              >
                {trans.signProgress.document} {index + 1}
                {currentDocInGroupIndex > index && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.7035 8.72866C13.9217 8.52405 13.9328 8.18126 13.7282 7.96302C13.5236 7.74478 13.1808 7.73373 12.9625 7.93834L8.8887 11.7577L7.0368 10.0217C6.81854 9.81705 6.47576 9.82812 6.27116 10.0464C6.06657 10.2646 6.07764 10.6074 6.29589 10.812L8.51827 12.8953C8.72663 13.0907 9.05085 13.0907 9.2592 12.8953L13.7035 8.72866Z"
                      fill="currentColor"
                    />
                    <path
                      d="M9.99967 1.9585C5.55838 1.9585 1.95801 5.55887 1.95801 10.0002C1.95801 14.4415 5.55838 18.0418 9.99967 18.0418C14.441 18.0418 18.0413 14.4415 18.0413 10.0002C18.0413 5.55887 14.441 1.9585 9.99967 1.9585ZM3.04134 10.0002C3.04134 6.15718 6.15669 3.04183 9.99967 3.04183C13.8427 3.04183 16.958 6.15718 16.958 10.0002C16.958 13.8431 13.8427 16.9585 9.99967 16.9585C6.15669 16.9585 3.04134 13.8431 3.04134 10.0002Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>

          <div className="sign-progress__title grouped">
            <div className="sign-progress__title-header">
              {trans.signProgress.document} {currentDocInGroup}
              {!!docName && <span>: </span>}
            </div>

            <div
              className={cx(
                'sign-progress__title-doc-name',
                !docName && 'sign-progress__title-doc-name--hidden'
              )}
            >
              {docName}
            </div>
          </div>
        </div>

        <div className="sign-progress__zoom-controls">{children}</div>
      </div>
    )
  }
)
